import { Loader2, Crown, ShieldCheck, Users } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Renders AI-generated markdown text cleanly on dark admin panels (bold, lists, headings,
// links) instead of showing raw ** and - characters.
export const AiMarkdown = ({ text, className = "" }) => (
  <div className={`text-sm leading-relaxed space-y-2 [&_p]:m-0 [&_strong]:text-white [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_h1]:text-white [&_h1]:font-semibold [&_h2]:text-white [&_h2]:font-semibold [&_h3]:text-gold [&_h3]:text-xs [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:font-semibold [&_h3]:mt-3 [&_a]:text-gold [&_a]:underline [&_code]:bg-white/10 [&_code]:px-1 [&_code]:rounded ${className}`}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text || ""}</ReactMarkdown>
  </div>
);


// Shared pipeline stage order (used by Overview, Projects and the orchestrator).
export const STAGES = ["New Inquiry", "Discovery", "Proposal Sent", "Proposal Accepted", "Design", "Development", "Revision", "Testing", "Launch", "Completed"];

// Relative timestamp: "Just now", "5 minutes ago", "2 hours ago", "Yesterday", weekday, or date.
export const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "Just now";
  if (s < 3600) { const m = Math.floor(s / 60); return `${m} minute${m === 1 ? "" : "s"} ago`; }
  if (s < 86400) { const h = Math.floor(s / 3600); return `${h} hour${h === 1 ? "" : "s"} ago`; }
  if (s < 172800) return "Yesterday";
  if (s < 604800) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString();
};

// Shared presentational primitives for the Admin dashboard sections.
export const Head = ({ title, desc, action }) => (
  <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
    <div><h1 className="font-heading text-3xl text-white">{title}</h1>{desc && <p className="text-gray-400 mt-1">{desc}</p>}</div>
    {action}
  </div>
);

export const tt = { background: "#172033", border: "1px solid #ffffff20", color: "#fff" };

export const Mini = ({ label, value, icon: Icon }) => (
  <div className="bg-navy-800 border border-white/5 p-5"><Icon className="text-gold mb-3" size={18} /><div className="font-heading text-2xl text-white">{value}</div><div className="text-gray-500 text-sm">{label}</div></div>
);

export const ChartCard = ({ title, children }) => (
  <div className="bg-navy-800 border border-white/5 p-6"><h3 className="font-heading text-lg text-white mb-6">{title}</h3>{children}</div>
);

export const NoData = ({ text = "Not enough data yet." }) => <div className="h-[260px] flex items-center justify-center text-gray-600 text-sm">{text}</div>;

export const Loader = () => <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={28} /></div>;

export const EmptyBox = ({ text }) => <div className="bg-navy-800 border border-white/5 p-16 text-center text-gray-500">{text}</div>;

export const KV = ({ k, v }) => <div><span className="text-xs uppercase tracking-wider text-gold">{k}</span><div className="text-gray-200">{v || "—"}</div></div>;

export const L = ({ label, children }) => <div><label className="text-sm text-gray-400 mb-1.5 block">{label}</label>{children}</div>;

export const roleMeta = (u) =>
  u.is_owner
    ? { label: "Owner", cls: "text-gold border-gold/40 bg-gold/10", Icon: Crown }
    : u.role === "admin"
    ? { label: "Admin", cls: "text-blue-300 border-blue-400/30 bg-blue-500/10", Icon: ShieldCheck }
    : { label: "Customer", cls: "text-gray-300 border-white/15 bg-white/5", Icon: Users };

export function ConfirmDialog({ open, title, message, confirmLabel = "Confirm", danger, onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-navy border border-white/15 max-w-md w-full p-6">
        <h3 className="font-heading text-xl text-white mb-2">{title}</h3>
        <p className="text-gray-400 text-sm mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm border border-white/20 text-gray-300 hover:bg-white/5 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="confirm-accept"
            className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60 transition-colors ${danger ? "bg-red-500 text-white hover:bg-red-600" : "bg-gold text-navy hover:bg-gold-light"}`}>
            {busy && <Loader2 className="animate-spin" size={14} />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export const IconBtn = ({ children, title, onClick, danger, testid }) => (
  <button onClick={onClick} title={title} data-testid={testid}
    className={`p-1.5 border transition-colors ${danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/15 text-gray-300 hover:border-gold/40 hover:text-gold"}`}>
    {children}
  </button>
);
